import type { ReactNode } from 'react';
import { Badge, makeStyles, tokens } from '@fluentui/react-components';
import { getAccount } from '@moko/utils/src/authHelper';
import { useTranslation } from 'react-i18next';
import PageHeader from '../Layout/PageHeader';
import SummaryCardsGrid, {
  type SummaryCardItem,
} from '../SummaryCards/SummaryCardsGrid';
import AttentionList from './AttentionList';
import RecentReports from './RecentReports';
import UpcomingVisits from './UpcomingVisits';
import WorkPipeline from './WorkPipeline';
import { dayPartFor } from './greeting';
import type { HomeLists } from './homeTypes';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    alignItems: 'start',
    gap: tokens.spacingVerticalL,
    '@media (min-width: 1201px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      columnGap: tokens.spacingHorizontalL,
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
    minWidth: 0,
  },
});

export interface HomePageProps extends HomeLists {
  kpiTitle: string;
  kpis: SummaryCardItem[];
  actions?: ReactNode;
  sampleData?: boolean;
}

const HomePage = ({
  kpiTitle,
  kpis,
  actions,
  sampleData,
  attention,
  pipeline,
  upcomingVisits,
  recentReports,
}: HomePageProps) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const greeting = {
    morning: t('goodMorning', 'Good morning'),
    afternoon: t('goodAfternoon', 'Good afternoon'),
    evening: t('goodEvening', 'Good evening'),
  }[dayPartFor(new Date().getHours())];
  const name = getAccount()?.name;

  return (
    <div className={styles.page}>
      <PageHeader
        title={name ? `${greeting}, ${name}` : greeting}
        subtitle={t('homeSubtitle', 'Here’s where your work stands today')}
        badge={
          sampleData ? (
            <Badge appearance="tint" color="informative">
              {t('sampleData', 'Sample data')}
            </Badge>
          ) : undefined
        }
        actions={actions}
      />
      <SummaryCardsGrid title={kpiTitle} items={kpis} />
      <div className={styles.columns}>
        <div className={styles.column}>
          <AttentionList items={attention} />
          <UpcomingVisits visits={upcomingVisits} />
        </div>
        <div className={styles.column}>
          <WorkPipeline stages={pipeline} />
          <RecentReports reports={recentReports} />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
